import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchProject, fetchEvaluations } from "../../api/memberApi";

const formatDate = (iso) => {
  if (!iso) return "—";
  return iso.substring(0, 10).replace(/-/g, "/");
};

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

function SubTable({ title, rows, nameKey }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableCell>{row[nameKey] || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const BAM_CHART_WIDTH = 600;
const BAM_CHART_HEIGHT = 260;
const BAM_PADDING = { top: 20, right: 20, bottom: 60, left: 50 };

function BamChart({ evaluations }) {
  if (!evaluations || evaluations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        制約スコアがまだ算定されていません
      </Typography>
    );
  }

  const scores = evaluations.map((e) => e.evaluationNumber / 1000);
  const maxScore = Math.max(...scores);
  const chartW = BAM_CHART_WIDTH - BAM_PADDING.left - BAM_PADDING.right;
  const chartH = BAM_CHART_HEIGHT - BAM_PADDING.top - BAM_PADDING.bottom;
  const barWidth = Math.min(60, (chartW / evaluations.length) - 8);
  const latestCreatedAt = evaluations.reduce((a, b) =>
    new Date(a.createdAt) > new Date(b.createdAt) ? a : b
  ).createdAt;

  return (
    <Box sx={{ mt: 2, overflowX: "auto" }}>
      <svg
        width={BAM_CHART_WIDTH}
        height={BAM_CHART_HEIGHT}
        style={{ display: "block" }}
      >
        <g transform={`translate(${BAM_PADDING.left},${BAM_PADDING.top})`}>
          {/* Y軸 */}
          <line x1={0} y1={0} x2={0} y2={chartH} stroke="#888" />
          {/* X軸 */}
          <line x1={0} y1={chartH} x2={chartW} y2={chartH} stroke="#888" />
          {/* Yラベル */}
          <text x={-40} y={0} fontSize={10} fill="#666" dominantBaseline="middle">1.0</text>
          <text x={-40} y={chartH / 2} fontSize={10} fill="#666" dominantBaseline="middle">0.5</text>
          <text x={-40} y={chartH} fontSize={10} fill="#666" dominantBaseline="middle">0.0</text>
          {/* バー */}
          {evaluations.map((e, i) => {
            const score = e.evaluationNumber / 1000;
            const barH = maxScore > 0 ? (score / maxScore) * chartH : 0;
            const x = (chartW / evaluations.length) * i + (chartW / evaluations.length - barWidth) / 2;
            const y = chartH - barH;
            const isMax = score === maxScore && maxScore > 0;
            const label = e.evaluationName || "";
            return (
              <g key={e.evaluationId || i}>
                <rect
                  x={x} y={y}
                  width={barWidth} height={barH}
                  fill={isMax ? "#d32f2f" : "#1976d2"}
                  rx={2}
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 4}
                  fontSize={10}
                  textAnchor="middle"
                  fill={isMax ? "#d32f2f" : "#333"}
                >
                  {score.toFixed(3)}
                </text>
                <text
                  x={x + barWidth / 2}
                  y={chartH + 14}
                  fontSize={10}
                  textAnchor="middle"
                  fill="#333"
                  transform={`rotate(-30, ${x + barWidth / 2}, ${chartH + 14})`}
                >
                  {label.length > 10 ? label.substring(0, 10) + "…" : label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        算定日時: {latestCreatedAt ? new Date(latestCreatedAt).toLocaleString("ja-JP") : "—"}
      </Typography>
    </Box>
  );
}

export default function ProjectDetailPage() {
  const { businessDomainId, businessUnitId, projectId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [evaluations, setEvaluations] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setItem(await fetchProject(projectId));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvaluations = useCallback(async () => {
    if (evaluations !== null) return;
    setEvalLoading(true);
    try {
      setEvaluations(await fetchEvaluations(projectId));
    } catch (e) {
      notify.apiError(e);
      setEvaluations([]);
    } finally {
      setEvalLoading(false);
    }
  }, [projectId, evaluations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (tab === 1) loadEvaluations();
  }, [tab, loadEvaluations]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (!item) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(
              `/strategy/${businessDomainId}/business-units/${businessUnitId}/projects`
            )
          }
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("project.detail.title")}
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab label="詳細" />
        <Tab label="BAM・制約スコア" />
      </Tabs>

      {tab === 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <DetailRow label={t("project.name")} value={item.projectName} />
            <DetailRow label={t("project.description")} value={item.projectDescription} />
            <DetailRow label={t("project.startDate")} value={formatDate(item.startDate)} />
            <DetailRow label={t("project.endDate")} value={formatDate(item.endDate)} />
          </Paper>
          <SubTable title={t("project.customerCategory.list.title")} rows={item.associatedCustomerCategories} nameKey="customerCategoryName" />
          <SubTable title={t("project.productCategory.list.title")} rows={item.associatedProductCategories} nameKey="productCategoryName" />
          <SubTable title={t("project.memberCategory.list.title")} rows={item.associatedMemberCategories} nameKey="memberCategoryName" />
          <SubTable title={t("project.partnerCategory.list.title")} rows={item.associatedPartnerCategories} nameKey="partnerCategoryName" />
          <SubTable title={t("project.placeCategory.list.title")} rows={item.associatedPlaceCategories} nameKey="placeCategoryName" />
          <SubTable title={t("project.dataCategory.list.title")} rows={item.associatedDataCategories} nameKey="dataCategoryName" />
          <SubTable title={t("project.applicationCategory.list.title")} rows={item.associatedApplicationCategories} nameKey="applicationCategoryName" />
          <SubTable title={t("project.financialAssetCategory.list.title")} rows={item.associatedFinancialAssetCategories} nameKey="financialAssetCategoryName" />
          <SubTable title={t("project.actionPlan.list.title")} rows={item.associatedActionPlans} nameKey="actionPlanName" />
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/projects/${projectId}/goals`)}
            >
              {t("project.goal.list.title")}
            </Button>
          </Box>
        </>
      )}

      {tab === 1 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            BAM・制約スコア
          </Typography>
          {evalLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <BamChart evaluations={evaluations} />
          )}
        </Paper>
      )}
    </Box>
  );
}
